import type { Ref, RefCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChevronUp, faChevronDown, faClockRotateLeft, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '@course-authoring/shared-types';
import type { PublishJobDto, LocaleOption } from '@course-authoring/shared-types';
import { summarizePublishJobStep, isActivePublishJob } from './publishJobSummaries';
import { PublishJobStatusChip, PublishJobStep } from './PublishJobPrimitives';
import type { PublishJobPopoverPlacement } from './PublishJobPrimitives';

export function PublishJobStatus({
  ready,
  job,
  languageOptions,
  isRunLogOpen,
  onToggleRunLog,
  onRetry,
  isRetrying,
  isMutationBlocked,
  retryDisabledReason,
  isDirty,
  isSaving,
  controls = 'publish-job-log',
  triggerRef,
  accessiblePrefix = 'Preparation status',
  showDraftState = true,
  stepLabels = {},
}: {
  ready: boolean;
  job: PublishJobDto | null;
  languageOptions: LocaleOption[];
  isRunLogOpen: boolean;
  onToggleRunLog: () => void;
  onRetry?: () => void;
  isRetrying: boolean;
  isMutationBlocked: boolean;
  retryDisabledReason?: string;
  isDirty: boolean;
  isSaving: boolean;
  controls?: string;
  triggerRef?: Ref<HTMLButtonElement>;
  accessiblePrefix?: string;
  showDraftState?: boolean;
  stepLabels?: Record<string, string>;
}): React.ReactElement {
  const { tokens } = useTheme();
  const statusLabel = !ready ? 'Checking status…' : !job ? null : job.status === 'queued' ? 'Queued'
    : job.status === 'completed' ? 'Ready'
      : job.status === 'failed' ? failureStatusLabel(job)
        : job.currentStep === 'translation' ? 'Translating' : job.currentStep === 'narration' ? 'Generating audio' : job.currentStep ? stepLabels[job.currentStep] ?? 'Preparing' : 'Preparing';
  const runningStep = job?.status === 'running' ? job.currentStep : null;
  const runningSummary = runningStep && job
    ? summarizePublishJobStep(runningStep, job.stepResults, languageOptions, job.result.targets?.[runningStep])
    : null;
  const statusCount = runningSummary?.progress
    ? `${runningSummary.progress.completed} of ${runningSummary.progress.total}`
    : null;
  const statusCountForScreenReader = runningSummary?.progress
    ? `${runningSummary.progress.completed} of ${runningSummary.progress.total} ${runningStep === 'translation' || runningStep === 'narration' ? 'languages' : 'items'}`
    : null;
  return (
    <span aria-live="polite" style={{ display: 'inline-flex', alignItems: 'center', gap: tokens.spacing.xs, flexWrap: 'nowrap', maxWidth: '100%' }}>
      {isSaving || !ready ? (
        <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: tokens.spacing.xs, color: tokens.text.muted, fontSize: 13 }}>
          <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />{isSaving ? 'Saving…' : 'Checking status…'}
        </span>
      ) : job && statusLabel ? (
        <PublishJobStatusChip
          state={job.status}
          label={statusLabel}
          accessibleLabel={`${accessiblePrefix}: ${statusLabel}${statusCountForScreenReader ? ` · ${statusCountForScreenReader}` : ''}`}
          progress={statusCount}
          expanded={isRunLogOpen}
          controls={controls}
          triggerRef={triggerRef}
          onClick={onToggleRunLog}
          onRetry={job.status === 'failed' ? onRetry : undefined}
          retrying={isRetrying}
          retryDisabled={isMutationBlocked || isDirty}
          retryDisabledReason={retryDisabledReason ?? (isDirty ? 'Save your draft before retrying' : 'Wait for preparation status to refresh')}
          draftState={showDraftState && isDirty ? 'Unsaved changes' : null}
          trailing={<FontAwesomeIcon icon={isRunLogOpen ? faChevronUp : faChevronDown} aria-hidden="true" style={{ fontSize: 10 }} />}
        />
      ) : showDraftState && isDirty ? <span role="status" style={{ color: tokens.text.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
        Unsaved changes
      </span> : null}
    </span>
  );
}

function failureStatusLabel(job: PublishJobDto): string {
  const failedOutcome = job.stepResults.find((outcome) => outcome.status === 'failed');
  const failedStep = failedOutcome?.step ?? job.result.attempts.find((attempt) => attempt.attempt === job.attempt)?.failedStep ?? job.currentStep;
  return failedStep === 'translation' ? 'Translation failed' : failedStep === 'narration' ? 'Audio failed' : 'Preparation failed';
}

export function PublishJobRunLog({
  job, languageOptions, onClose, steps, stepLabels = {}, copy, notice,
  id = 'publish-job-log', popover,
}: {
  job: PublishJobDto;
  languageOptions: LocaleOption[];
  onClose: () => void;
  steps: string[];
  stepLabels?: Record<string, string>;
  copy?: { preparing: string; ready: string; failureDescription: string };
  notice?: string;
  id?: string;
  popover?: { placement: PublishJobPopoverPlacement; panelRef: RefCallback<HTMLElement> };
}): React.ReactElement {
  const { tokens } = useTheme();
  const labels = new Map(languageOptions.map(({ locale, label }) => [locale, label]));
  const languageName = (locale: string) => labels.get(locale) ?? locale;
  const currentAttempt = job.result.attempts.find((attempt) => attempt.attempt === job.attempt);
  const failedOutcome = job.stepResults.find((result) => result.status === 'failed');
  const failureStep = failedOutcome?.step ?? currentAttempt?.failedStep ?? job.currentStep;
  const failureLocale = failedOutcome?.locale;
  const previousAttempts = job.result.attempts.filter((attempt) => attempt.attempt !== job.attempt && attempt.status === 'failed');
  const timeLabel = (value: string | null, prefix: string) => value ? `${prefix} ${new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : null;
  const meta = [timeLabel(job.startedAt, 'Started'), timeLabel(job.finishedAt, job.status === 'failed' ? 'Failed' : 'Finished'), `Attempt ${job.attempt}`].filter(Boolean).join(' · ');
  const title = job.status === 'failed' ? 'Preparation stopped'
    : job.status === 'completed' ? copy?.ready ?? 'Preparation is ready' : copy?.preparing ?? 'Preparing content';
  const errorTitle = failureLocale
    ? `${languageName(failureLocale)} ${failureStep === 'translation' ? "couldn't be translated" : failureStep === 'narration' ? 'audio could not be prepared' : 'could not be prepared'}`
    : failureStep === 'translation' ? 'Translation failed' : failureStep === 'narration' ? 'Audio narration failed' : 'Preparation failed';
  const diagnosticCode = failedOutcome?.code ?? currentAttempt?.code;
  const diagnosticMessage = failedOutcome?.message ?? currentAttempt?.message;
  const content = (
    <section ref={popover?.panelRef} id={id} role={popover ? 'dialog' : undefined} tabIndex={popover ? -1 : undefined} aria-label="Preparation details" style={{ margin: popover ? 0 : `0 ${tokens.spacing.lg} ${tokens.spacing.sm}`, border: `1px solid ${tokens.borders.default}`, borderRadius: tokens.radius.md, maxHeight: popover ? Math.min(360, popover.placement.maxHeight) : 360, overflow: 'hidden', flexShrink: 0, display: 'flex', flexDirection: 'column', background: tokens.backgrounds.page, color: tokens.text.primary, fontFamily: tokens.typography.fontFamily, fontSize: 13, ...(popover ? { position: 'fixed', zIndex: 1200, left: popover.placement.left, width: popover.placement.width, maxWidth: popover.placement.width, boxSizing: 'border-box', boxShadow: '0 8px 24px rgba(23,25,50,0.16)', ...('top' in popover.placement ? { top: popover.placement.top } : { bottom: popover.placement.bottom }) } : {}) }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing.sm, padding: tokens.spacing.sm, borderBottom: `1px solid ${tokens.borders.default}`, flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}><strong>{title}</strong><div style={{ fontSize: 12, color: tokens.text.muted }}>{meta}</div></div>
        <button type="button" aria-label={popover ? 'Close preparation details' : 'Hide preparation details'} aria-expanded="true" aria-controls={id} onClick={onClose} style={{ width: 28, height: 28, border: 0, borderRadius: tokens.radius.sm, background: 'transparent', color: tokens.text.muted, cursor: 'pointer' }}><FontAwesomeIcon icon={faChevronUp} aria-hidden="true" /></button>
      </header>
      <div style={{ minHeight: 0, flex: '1 1 auto', overflowY: 'auto', padding: `0 ${tokens.spacing.sm}` }}>
        {notice && <div style={{ marginTop: tokens.spacing.sm, color: tokens.text.muted, fontSize: 13 }}>{notice}</div>}
        {job.status === 'failed' && <div role="alert" style={{ marginTop: tokens.spacing.sm, padding: tokens.spacing.sm, borderRadius: tokens.radius.md, background: tokens.ui.errorBackground, border: `1px solid ${tokens.ui.errorBorder}`, color: tokens.text.primary }}>
          <strong style={{ color: tokens.ui.errorText }}>{errorTitle}</strong>
          <div style={{ fontSize: 13 }}>{copy?.failureDescription ?? 'Preparation stopped. Saved content remains editable; successful outputs from this attempt may already be available. Retry preparation starts the workflow again.'}</div>
          {(diagnosticCode || diagnosticMessage) && <details style={{ marginTop: tokens.spacing.xs, fontSize: 12 }}><summary style={{ cursor: 'pointer' }}>Technical details</summary><div style={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{[diagnosticCode, diagnosticMessage].filter(Boolean).join(' · ')}</div></details>}
        </div>}
        {steps.map((step) => {
          const outcomes = job.stepResults.filter((result) => result.step === step);
          const summary = summarizePublishJobStep(
            step,
            job.stepResults,
            languageOptions,
            job.result.targets?.[step],
          );
          const failed = outcomes.some((result) => result.status === 'failed') || (job.status === 'failed' && failureStep === step);
          const running = isActivePublishJob(job) && job.currentStep === step;
          const currentStepIndex = steps.indexOf(job.currentStep ?? '');
          const pending = isActivePublishJob(job) && (job.status === 'queued' || (currentStepIndex >= 0 && steps.indexOf(step) > currentStepIndex));
          const done = job.status === 'completed' || (!running && !pending && outcomes.length > 0 && !failed);
          const titleForStep = stepLabels[step] ?? (step === 'translation' ? 'Translation' : step === 'narration' ? 'Audio narration' : step);
          const summaryText = running
            ? summary.progress ? `${summary.progress.completed} of ${summary.progress.total} ${step === 'translation' || step === 'narration' ? 'languages' : 'items'}` : step === 'translation' || step === 'narration' ? 'Preparing languages' : 'In progress'
            : pending ? 'Waiting to start'
              : isActivePublishJob(job) && currentStepIndex < 0 ? 'Waiting for the current step to be reported'
                : job.status === 'failed' && failureStep === null ? 'Preparation stopped before a step was recorded' : summary.summary;
          return <PublishJobStep key={step} title={titleForStep} summary={summaryText} progress={summary.progress}
            failed={failed} done={done} running={running} languages={summary.languages}>
            {summary.noVoiceSentence && <div style={{ marginTop: tokens.spacing.xs, fontSize: 12, color: tokens.text.muted }}>{summary.noVoiceSentence}</div>}
            {summary.otherSkipSentences.map((sentence) => <div key={sentence} style={{ marginTop: tokens.spacing.xs, fontSize: 12, color: tokens.text.muted }}>{sentence}</div>)}
          </PublishJobStep>;
        })}
        {previousAttempts.map((attempt) => <div key={attempt.attempt} style={{ padding: `${tokens.spacing.xs} 0`, color: tokens.text.muted, fontSize: 12 }}><FontAwesomeIcon icon={faClockRotateLeft} aria-hidden="true" style={{ marginRight: tokens.spacing.xs }} />Attempt {attempt.attempt} also stopped at {attempt.failedStep === 'translation' ? 'Translation' : attempt.failedStep === 'narration' ? 'Audio narration' : attempt.failedStep ? stepLabels[attempt.failedStep] ?? attempt.failedStep : 'preparation'}</div>)}
      </div>
    </section>
  );
  return popover ? createPortal(content, document.body) : content;
}

export function PublishJobStatusWarning({ message }: { message: string }): React.ReactElement {
  const { tokens } = useTheme();
  return <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacing.sm, padding: tokens.spacing.sm, border: `1px solid color-mix(in srgb, ${tokens.ui.workflowDraft} 45%, ${tokens.backgrounds.page})`, borderRadius: tokens.radius.md, background: `color-mix(in srgb, ${tokens.ui.workflowDraft} 9%, ${tokens.backgrounds.page})`, color: tokens.text.primary, fontSize: 13, lineHeight: 1.4 }}><FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ color: tokens.ui.workflowDraft, marginTop: 2, flexShrink: 0 }} /><span>{message}</span></div>;
}
